import { Navbar } from "../../../containers/home/navigation";
import { Footer } from "../../../containers/home/footer";
import {
  PrivacyPolicyWrapper,
  PolicyContainer,
  PolicyTitle,
  PolicySection,
} from "./styled";

export const PrivacyPolicy = () => {
  return (
    <>
      <Navbar />
      <PrivacyPolicyWrapper>
        <PolicyContainer>
          <PolicyTitle>Privacy Policy</PolicyTitle>
          <p
            style={{
              textAlign: "center",
              fontStyle: "italic",
              color: "#666",
              marginBottom: "40px",
            }}
          >
            workPlacePAY – Employee Payslip Management Platform
            <br />
            Effective Date: May 13, 2026
          </p>

          <PolicySection>
            <h2>1. Introduction</h2>
            <p>
              WorkplacePay ("we", "our", or "us") provides a cloud-based
              employee payslip management platform that enables companies
              ("Employers") to securely distribute payslips and payroll-related
              information to their employees ("Employees"). This Privacy Policy
              explains how we collect, use, store, share, and protect personal
              data when you use our platform, whether you are an Employer or an
              Employee.
            </p>
            <p>
              By accessing or using WorkplacePay, you acknowledge that you have
              read and understood this Privacy Policy. If you do not agree with
              our practices, please discontinue use of the platform.
            </p>
          </PolicySection>

          <PolicySection>
            <h2>2. Data Controller and Data Processor</h2>
            <p>
              WorkplacePay acts as a Data Processor on behalf of Employers, who
              are the Data Controllers for their employees' personal data. This
              means:
            </p>
            <ul>
              <li>
                Employers determine the purpose and means of processing employee
                personal data.
              </li>
              <li>
                WorkplacePay processes data only according to the instructions
                of the Employer and applicable law.
              </li>
              <li>
                Both parties share responsibilities to protect personal data
                under applicable data protection laws including the Nigeria Data
                Protection Act (NDPA), the EU General Data Protection Regulation
                (GDPR), and other applicable global regulations.
              </li>
            </ul>
          </PolicySection>

          <PolicySection>
            <h2>3. Data We Collect</h2>
            <p>
              We collect and process the following categories of personal data:
            </p>

            <h3>3.1 Employee Data (provided by Employers)</h3>
            <ul>
              <li>Full name, job title, and employee ID</li>
              <li>Contact information (email address, phone number)</li>
              <li>Bank account details and payment information</li>
              <li>Tax identification numbers and government-issued IDs</li>
              <li>Salary, allowances, deductions, and payslip information</li>
              <li>Employment status and contract information</li>
            </ul>

            <h3>3.2 Authentication and Access Data</h3>
            <ul>
              <li>
                Login credentials (username/email and encrypted passwords)
              </li>
              <li>
                Biometric authentication data where enabled (e.g. fingerprint or
                facial recognition for mobile login)
              </li>
              <li>Device identifiers, IP addresses, and session tokens</li>
            </ul>

            <h3>3.3 Usage and Technical Data</h3>
            <ul>
              <li>
                Log data including access times, pages viewed, and actions
                performed
              </li>
              <li>Device type, operating system, and app version</li>
              <li>Crash reports and performance data</li>
            </ul>
          </PolicySection>

          <PolicySection>
            <h2>4. How We Use Your Data</h2>
            <p>We use the data we collect for the following purposes:</p>
            <ul>
              <li>
                To provide, operate, and maintain the WorkplacePay platform
              </li>
              <li>To generate and securely deliver payslips to employees</li>
              <li>To authenticate users and protect account security</li>
              <li>
                To enable Employers to manage payroll records and employee data
              </li>
              <li>To comply with legal and regulatory obligations</li>
              <li>To improve our platform performance and user experience</li>
              <li>
                To detect, investigate, and prevent fraudulent or unauthorized
                activity
              </li>
              <li>
                To communicate service updates, security alerts, and support
                messages
              </li>
            </ul>
          </PolicySection>

          <PolicySection>
            <h2>5. Legal Basis for Processing</h2>
            <p>We process personal data under the following legal bases:</p>
            <ul>
              <li>
                Contractual necessity: Processing is required to deliver payslip
                services as agreed between WorkplacePay and the Employer.
              </li>
              <li>
                Legal obligation: To comply with applicable employment, tax, and
                data protection laws.
              </li>
              <li>
                Legitimate interests: To secure the platform, prevent fraud, and
                improve our services.
              </li>
              <li>
                Consent: Where required (e.g. biometric authentication), we
                obtain explicit consent from the individual prior to processing.
              </li>
            </ul>
          </PolicySection>

          <PolicySection>
            <h2>6. Data Sharing and Disclosure</h2>
            <p>
              We do not sell or rent personal data to third parties. We may
              share data in the following circumstances:
            </p>
            <ul>
              <li>
                With Employers: Employee data is accessible to the Employer who
                uploaded it, solely for payroll management purposes.
              </li>
              <li>
                With service providers: We may engage trusted third-party
                vendors (e.g. cloud hosting, security, analytics) who process
                data strictly on our behalf under data processing agreements.
              </li>
              <li>
                For legal compliance: We may disclose data when required by law,
                court order, or regulatory authority.
              </li>
              <li>
                Business transfers: In the event of a merger, acquisition, or
                sale of assets, data may be transferred with appropriate notice
                and protections.
              </li>
            </ul>
          </PolicySection>

          <PolicySection>
            <h2>7. Data Security</h2>
            <p>
              We implement industry-standard security measures to protect your
              personal data, including:
            </p>
            <ul>
              <li>
                End-to-end encryption for data in transit (TLS 1.2+) and at rest
                (AES-256)
              </li>
              <li>Multi-factor authentication for Employer admin accounts</li>
              <li>
                Role-based access control to limit data access on a need-to-know
                basis
              </li>
              <li>Regular security audits and penetration testing</li>
              <li>
                Secure biometric data storage with on-device processing where
                possible
              </li>
            </ul>
            <p>
              Despite these measures, no system is completely secure. We
              encourage users to use strong passwords and report any suspected
              security incidents to us immediately.
            </p>
          </PolicySection>

          <PolicySection>
            <h2>8. Data Retention</h2>
            <p>
              We retain personal data only for as long as necessary to fulfil
              the purposes outlined in this policy, or as required by applicable
              law. Typical retention periods include:
            </p>
            <ul>
              <li>
                Payslip and payroll records: 7 years (in line with common
                financial record-keeping requirements)
              </li>
              <li>
                Account and login data: Duration of the Employer's active
                subscription, plus 90 days after termination
              </li>
              <li>
                Biometric data: Deleted immediately upon employee account
                deactivation or withdrawal of consent
              </li>
              <li>Log and technical data: Up to 12 months</li>
            </ul>
            <p>
              Employers may request deletion of their organization's data at any
              time subject to legal retention requirements.
            </p>
          </PolicySection>

          <PolicySection>
            <h2>9. International Data Transfers</h2>
            <p>
              As a global platform, personal data may be transferred to and
              processed in countries outside your jurisdiction. When
              transferring data internationally, we ensure appropriate
              safeguards are in place, including:
            </p>
            <ul>
              <li>
                Standard Contractual Clauses (SCCs) approved by relevant data
                protection authorities
              </li>
              <li>Adequacy decisions where applicable</li>
              <li>Data processing agreements with all sub-processors</li>
            </ul>
          </PolicySection>

          <PolicySection>
            <h2>10. Your Rights</h2>
            <p>
              Depending on your location, you may have the following rights
              regarding your personal data:
            </p>
            <ul>
              <li>
                Right to access: Request a copy of the personal data we hold
                about you
              </li>
              <li>
                Right to rectification: Request correction of inaccurate or
                incomplete data
              </li>
              <li>
                Right to erasure: Request deletion of your personal data,
                subject to legal obligations
              </li>
              <li>
                Right to restrict processing: Request that we limit how we use
                your data
              </li>
              <li>
                Right to data portability: Receive your data in a structured,
                machine-readable format
              </li>
              <li>
                Right to object: Object to processing based on legitimate
                interests
              </li>
              <li>
                Right to withdraw consent: Withdraw consent at any time where
                processing is consent-based (e.g. biometrics)
              </li>
            </ul>
            <p>
              Employees should direct rights requests to their Employer in the
              first instance. Employers and individuals may also contact us
              directly at the details below. We will respond within 30 days.
            </p>
          </PolicySection>

          <PolicySection>
            <h2>11. Children's Privacy</h2>
            <p>
              WorkplacePay is intended solely for use by adults in an employment
              context. We do not knowingly collect personal data from
              individuals under the age of 18. If you believe a minor's data has
              been processed through our platform, please contact us
              immediately.
            </p>
          </PolicySection>

          <PolicySection>
            <h2>12. Cookies and Tracking Technologies</h2>
            <p>
              Our web platform uses cookies and similar technologies to maintain
              sessions, remember preferences, and analyze usage patterns. You
              can control cookie settings through your browser. Disabling
              cookies may affect some platform features. Our mobile app does not
              use browser cookies but may use similar device-level identifiers
              for analytics and crash reporting.
            </p>
          </PolicySection>

          <PolicySection>
            <h2>13. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will
              notify Employers of material changes via email and in-app
              notification at least 30 days before the changes take effect.
              Continued use of the platform after the effective date constitutes
              acceptance of the updated policy. The most current version will
              always be available within the app and on our website.
            </p>
          </PolicySection>

          <PolicySection>
            <h2>14. Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests relating to this
              Privacy Policy or our data practices, please contact us at:
            </p>
            <p>
              <strong>WorkplacePay – Data Privacy Team</strong>
              <br />
              <strong>Email:</strong> workplacepay@focusgroupng.com
              <br />
              <strong>Phone:</strong> +234 8021810040
            </p>
            <p>
              For users in the European Economic Area (EEA), you also have the
              right to lodge a complaint with your local data protection
              authority.
            </p>
          </PolicySection>

          <PolicySection>
            <p
              style={{
                textAlign: "center",
                fontWeight: "600",
                marginTop: "40px",
              }}
            >
              © 2026 WorkplacePay. All rights reserved.
            </p>
          </PolicySection>
        </PolicyContainer>
      </PrivacyPolicyWrapper>
      <Footer />
    </>
  );
};
